import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faPlus, faMinus } from '@fortawesome/free-solid-svg-icons'
import {
  HeaderBackground,
  Navbar,
  Spinner,
  ErrorNotification,
} from '../components'
import { getPovratProizvodaService } from '../services/PovratProizvoda'
import { getPovratDokumentiService } from '../services/PovratDokument'

const PDV_RATE = 0.17
const MIN_TABLE_ROWS = 5

// 48.00 -> "48,00"
const formatKM = value =>
  Number(value || 0)
    .toFixed(2)
    .replace('.', ',')

export default function PovratProizvoda() {
  const token = useSelector(state => state.authentication.token?.access_token)

  const [products, setProducts] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isForbidden, setIsForbidden] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)

  const [brojDokumenta, setBrojDokumenta] = useState('')
  const [info, setInfo] = useState('')
  const [datum, setDatum] = useState(moment().format('DD.MM.YYYY.'))
  const [search, setSearch] = useState('')
  const [items, setItems] = useState([])
  const [dokumenti, setDokumenti] = useState([])
  const [dokumentSearch, setDokumentSearch] = useState('')

  const fetchProducts = useCallback(async () => {
    setIsLoading(true)
    const result = await getPovratProizvodaService({ accessToken: token })
    if (result.isOk) setProducts(result.data)
    else if (result.status === 403) setIsForbidden(true)
    else setErrorMessage(result.message)
    setIsLoading(false)
  }, [token])

  const fetchDokumenti = useCallback(async () => {
    const result = await getPovratDokumentiService({ accessToken: token })
    if (result.isOk) setDokumenti(result.data)
    else if (result.status === 403) setIsForbidden(true)
    else setErrorMessage(result.message)
  }, [token])

  useEffect(() => {
    if (token) {
      fetchProducts()
      fetchDokumenti()
    }
  }, [token, fetchProducts, fetchDokumenti])

  const filteredProducts =
    search.trim() === ''
      ? []
      : products
          .filter(product => !items.some(item => item.id === product.id))
          .filter(product =>
            `${product.name} ${product.code}`
              .toLowerCase()
              .includes(search.trim().toLowerCase()),
          )
          .slice(0, 8)

  const filteredDokumenti =
    dokumentSearch.trim() === ''
      ? []
      : dokumenti
          .filter(dokument =>
            [
              dokument.klijent_naziv,
              dokument.klijent_adresa,
              dokument.klijent_grad,
              dokument.klijent_zip,
              dokument.klijent_id,
            ]
              .filter(Boolean)
              .join(' ')
              .toLowerCase()
              .includes(dokumentSearch.trim().toLowerCase()),
          )
          .slice(0, 8)

  const selectDokument = dokument => {
    const infoLines = [
      dokument.klijent_naziv,
      dokument.klijent_adresa,
      [dokument.klijent_zip, dokument.klijent_grad].filter(Boolean).join(' '),
      dokument.klijent_id ? `ID : ${dokument.klijent_id}` : null,
    ].filter(line => line && `${line}`.trim() !== '')

    setInfo(infoLines.join('\n'))
    setDokumentSearch('')
  }

  const addItem = product => {
    setItems([...items, { ...product, kolicina: 1 }])
    setSearch('')
  }

  const updateKolicina = (id, kolicina) => {
    setItems(items.map(item => (item.id === id ? { ...item, kolicina } : item)))
  }

  const stepKolicina = (id, delta) => {
    setItems(
      items.map(item =>
        item.id === id
          ? {
              ...item,
              kolicina: Math.max(0, Number(item.kolicina || 0) + delta),
            }
          : item,
      ),
    )
  }

  const removeItem = id => {
    setItems(items.filter(item => item.id !== id))
  }

  // Cijene sadrze PDV, pa se osnovica izvlaci iz ukupnog iznosa
  // (kao u dokumentu: 139,20 -> 118,97 + 20,23)
  const ukupnoSaPdv = items.reduce(
    (sum, item) => sum + Number(item.kolicina || 0) * Number(item.price || 0),
    0,
  )
  const iznosBezPdv = ukupnoSaPdv / (1 + PDV_RATE)
  const pdv = ukupnoSaPdv - iznosBezPdv

  const emptyRowsCount = Math.max(0, MIN_TABLE_ROWS - items.length)

  if (!token) {
    return (
      <>
        <Navbar />
        <HeaderBackground title="Povrat proizvoda" />
        <div className="section">
          <div className="container">
            <p className="has-text-centered">
              Morate biti prijavljeni kako biste pristupili ovoj stranici.
            </p>
          </div>
        </div>
      </>
    )
  }

  if (isForbidden) {
    return (
      <>
        <Navbar />
        <HeaderBackground title="Povrat proizvoda" />
      </>
    )
  }

  return (
    <>
      <Navbar />
      <HeaderBackground title="Povrat proizvoda" />
      <div className="section" id="povrat_proizvoda">
        <div className="container">
          <div className="no-print">
            <div className="buttons">
              <Link to="/povrat-proizvoda-forma" className="button">
                Povrat proizvoda - forma
              </Link>
              <Link to="/povrat-dokumenti" className="button">
                Klijenti
              </Link>
            </div>

            <hr />

            <div className="field" style={{ maxWidth: '400px' }}>
              <label className="label">Broj dokumenta</label>
              <div className="control">
                <input
                  className="input"
                  type="text"
                  placeholder="KO 03/2026"
                  value={brojDokumenta}
                  onChange={event => setBrojDokumenta(event.target.value)}
                />
              </div>
            </div>

            <div className="field" style={{ maxWidth: '400px' }}>
              <label className="label">Pretraga klijenata</label>
              <div className="control">
                <input
                  className="input"
                  type="text"
                  placeholder="Pretraži po nazivu, adresi, gradu..."
                  value={dokumentSearch}
                  onChange={event => setDokumentSearch(event.target.value)}
                />
              </div>
              {filteredDokumenti.length > 0 && (
                <div className="ko-search-results">
                  {filteredDokumenti.map(dokument => (
                    <button
                      key={dokument.id}
                      type="button"
                      className="ko-search-result"
                      onClick={() => selectDokument(dokument)}>
                      {[
                        dokument.klijent_naziv,
                        dokument.klijent_adresa,
                        dokument.klijent_grad,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </button>
                  ))}
                </div>
              )}
              {dokumentSearch.trim() !== '' &&
                filteredDokumenti.length === 0 && (
                  <p className="mt-2">Nema rezultata pretrage.</p>
                )}
            </div>

            <div className="field" style={{ maxWidth: '400px' }}>
              <label className="label">Info (naziv, adresa, ID...)</label>
              <div className="control">
                <textarea
                  className="textarea"
                  rows={4}
                  placeholder={
                    'BINGO P.J. 279\nPoslovni Centar 96\n72250 Vitez\nID: 4209253456192'
                  }
                  value={info}
                  onChange={event => setInfo(event.target.value)}
                />
              </div>
            </div>

            <div className="field" style={{ maxWidth: '400px' }}>
              <label className="label">Datum</label>
              <div className="control">
                <input
                  className="input"
                  type="text"
                  placeholder="01.07.2026."
                  value={datum}
                  onChange={event => setDatum(event.target.value)}
                />
              </div>
            </div>

            <div className="field" style={{ maxWidth: '400px' }}>
              <label className="label">Pretraga proizvoda</label>
              <div className="control">
                <input
                  className="input"
                  type="text"
                  placeholder="Pretražite po nazivu ili šifri..."
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                />
              </div>
              {filteredProducts.length > 0 && (
                <div className="ko-search-results">
                  {filteredProducts.map(product => (
                    <button
                      key={product.id}
                      type="button"
                      className="ko-search-result"
                      onClick={() => addItem(product)}>
                      {`${product.name} (${product.code}) — ${formatKM(
                        product.price,
                      )} KM`}
                    </button>
                  ))}
                </div>
              )}
              {search.trim() !== '' && filteredProducts.length === 0 && (
                <p className="mt-2">Nema rezultata pretrage.</p>
              )}
            </div>

            {items.length > 0 && (
              <table className="table">
                <thead>
                  <tr>
                    <th>Naziv</th>
                    <th>Šifra</th>
                    <th>Cijena</th>
                    <th>Količina</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>{item.code}</td>
                      <td>{`${formatKM(item.price)} KM`}</td>
                      <td>
                        <div className="field has-addons mb-0">
                          <div className="control">
                            <button
                              className="button is-small"
                              type="button"
                              onClick={() => stepKolicina(item.id, -1)}>
                              <FontAwesomeIcon icon={faMinus} />
                            </button>
                          </div>
                          <div className="control">
                            <input
                              className="input is-small has-text-centered"
                              style={{ width: '60px' }}
                              type="number"
                              min="0"
                              value={item.kolicina}
                              onChange={event =>
                                updateKolicina(item.id, event.target.value)
                              }
                            />
                          </div>
                          <div className="control">
                            <button
                              className="button is-small"
                              type="button"
                              onClick={() => stepKolicina(item.id, 1)}>
                              <FontAwesomeIcon icon={faPlus} />
                            </button>
                          </div>
                        </div>
                      </td>
                      <td>
                        <button
                          className="button is-small"
                          type="button"
                          onClick={() => removeItem(item.id)}>
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {isLoading && (
              <Spinner
                note="Procesiranje..."
                customStyle={{ marginTop: '2rem' }}
              />
            )}
            {errorMessage && <ErrorNotification errorMessage={errorMessage} />}

            <button
              className="button greenButton mt-4"
              type="button"
              onClick={() => window.print()}>
              Print
            </button>

            <hr />

            <h2 className="subtitle">Pregled dokumenta</h2>
          </div>

          <div className="ko-document">
            {/* thead/tfoot spaceri se ponavljaju na svakoj stampanoj stranici
                i simuliraju gornju/donju marginu (uz @page margin: 0) */}
            <table className="ko-print-layout">
              <thead>
                <tr>
                  <td>
                    <div className="ko-print-space" />
                  </td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div className="ko-company">
                      <p className="ko-company-name">
                        Poljoprivredna proizvodnja „Korpa zdravlja“, vl. Alen
                        Dinar
                      </p>
                      <p>Bradve 69, 71305 Donje Moštre, Visoko</p>
                      <p className="ko-company-ids">
                        ID: 4319847000008 | PDV: 319847000008
                      </p>
                    </div>

                    <div className="ko-header">
                      <div className="ko-header-left">
                        <h2 className="ko-broj">
                          {brojDokumenta || 'KO __/____'}
                        </h2>
                        <p className="ko-datum">{`Datum : ${datum}`}</p>
                      </div>
                      <div className="ko-info">{info}</div>
                    </div>

                    <table className="ko-table">
                      <thead>
                        <tr>
                          <th className="ko-col-rbr">R.br.</th>
                          <th className="ko-col-naziv">Naziv artikla</th>
                          <th className="ko-col-sifra">Šifra</th>
                          <th className="ko-col-kolicina">Količina:</th>
                          <th className="ko-col-cijena">Cijena:</th>
                          <th className="ko-col-ukupno">Ukupno:</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => (
                          <tr key={item.id}>
                            <td>{index + 1}</td>
                            <td>{item.name}</td>
                            <td>{item.code}</td>
                            <td>{item.kolicina}</td>
                            <td>{formatKM(item.price)}</td>
                            <td>
                              {formatKM(
                                Number(item.kolicina || 0) *
                                  Number(item.price || 0),
                              )}
                            </td>
                          </tr>
                        ))}
                        {[...Array(emptyRowsCount)].map((_, index) => (
                          <tr key={`empty-${index}`}>
                            <td>&nbsp;</td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td></td>
                          </tr>
                        ))}
                      </tbody>
                      <tbody className="ko-summary">
                        <tr>
                          <td colSpan="5">IZNOS BEZ PDV:</td>
                          <td>{formatKM(iznosBezPdv)}</td>
                        </tr>
                        <tr>
                          <td colSpan="5">PDV:</td>
                          <td>{formatKM(pdv)}</td>
                        </tr>
                        <tr>
                          <td colSpan="5">UKUPNO SA PDV:</td>
                          <td>{formatKM(ukupnoSaPdv)}</td>
                        </tr>
                      </tbody>
                    </table>

                    <p className="ko-note">
                      {`Izvršili smo ispravku-umanjenje naše obaveze za izlazni PDV i
              molimo Vas da svojim potpisom potvrdite Vašu saglasnost i
              korekciju storno ulaznog PDV-a sa Vaše strane u iznosu
              ${formatKM(pdv)} KM .`}
                    </p>

                    <div className="ko-signatures">
                      <p className="ko-saglasnost">
                        Odobrio : <span className="ko-line" />
                      </p>
                      <p className="ko-mp">
                        M.P. <span className="ko-line" />
                      </p>
                    </div>

                    <div className="ko-footer">
                      <p className="ko-mp">
                        M.P. <span className="ko-line" />
                      </p>
                      <p className="ko-vlasnik">
                        Vlasnik : <span className="ko-line" />
                      </p>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    <div className="ko-print-space" />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </>
  )
}
